import cn from 'classnames'
import {FC} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonSize} from '@/components/common/content/ButtonTypes'
import {TextFontWeight} from '@/components/common/content/Text'
import {ArrowIcon} from '@/components/common/icons/ArrowIcon'
import {SubmenuProps, useHeaderContent} from '@/content/header'
import {Link, useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

import {SubmenuItem} from './SubmenuItem'
import {SubmenuSection} from './SubmenuSection'

const LIST_MARGIN_TOP = '36px'

export const ServicesSubmenu: FC<SubmenuProps> = ({closeNavigation}) => {
  const {t} = useTranslation('page')
  const {
    submenu: {sections, hasEvenSpacing},
  } = useHeaderContent().menu.navigation.services

  return (
    <>
      <div className={cn('wrapper', hasEvenSpacing && 'even-spacing')}>
        {Object.entries(sections).map(([key, section]) => {
          const {title, links, numOfColumns, button, isAlignedWithList} = section
          const hasMultipleColumns = numOfColumns > 1

          return (
            <SubmenuSection
              key={key}
              title={t(title)}
              isWide={hasMultipleColumns}
              isContentSized={hasEvenSpacing}
            >
              <ul className={cn(hasMultipleColumns && 'list', isAlignedWithList && 'aligned-list')}>
                {links.map(({icon, title, description, linkHref, trackedEvent, isHighlighted}) => (
                  <li
                    key={title}
                    className={cn(
                      hasMultipleColumns ? 'bullet-point' : 'item',
                      isHighlighted && 'highlighted',
                    )}
                  >
                    <SubmenuItem
                      icon={icon}
                      title={{text: t(title)}}
                      description={t(description)}
                      linkHref={linkHref}
                      onClick={() => {
                        trackedEvent && analytics.track(trackedEvent)
                        closeNavigation && closeNavigation()
                      }}
                      isHighlighted={isHighlighted}
                      isBulletPoint={hasMultipleColumns}
                      hasIndent={!isAlignedWithList}
                    />
                  </li>
                ))}
              </ul>
              {button && (
                <div className="button">
                  <Link href={button.linkHref}>
                    <Button
                      type="button"
                      size={ButtonSize.small}
                      icon={<ArrowIcon color={theme.palette.primary.main} />}
                      isHoverable
                      fontWeight={TextFontWeight.medium}
                      textAlign="left"
                    >
                      {t(button.linkText)}
                    </Button>
                  </Link>
                </div>
              )}
            </SubmenuSection>
          )
        })}
      </div>

      <style jsx>{`
        .wrapper {
          display: flex;
        }
        .wrapper.even-spacing {
          flex: 1;
          justify-content: space-between;
        }
        .even-spacing .list {
          display: grid;
          grid-template-columns: repeat(2, max-content);
          column-gap: ${theme.spacing[5]};
        }
        .even-spacing .bullet-point {
          max-width: none;
        }
        .list {
          display: flex;
          flex-wrap: wrap;
          margin-top: ${LIST_MARGIN_TOP};
          margin-left: ${theme.spacing[1.5]};
          column-gap: ${theme.spacing[2.5]};
          row-gap: ${theme.spacing[4]};
        }
        .item + .item {
          margin-top: ${theme.spacing[2.5]};
        }
        .aligned-list {
          margin-top: ${LIST_MARGIN_TOP};
        }
        .bullet-point {
          width: 100%;
          max-width: calc(50% - ${theme.spacing[1.5]});
        }
        .highlighted {
          margin-bottom: ${theme.spacing[3.5]};
        }
        .button {
          margin-top: ${theme.spacing[5]};
          margin-left: ${theme.spacing[1.5]};
        }
        @media ${TABLET_SCREENSIZE} {
          .wrapper {
            display: block;
          }
          .list {
            margin-top: ${theme.spacing[4]};
            margin-left: ${theme.spacing[4]};
          }
          .aligned-list {
            margin-top: 0;
          }
          .even-spacing .list {
            grid-template-columns: repeat(2, 1fr);
            column-gap: ${theme.spacing[2.5]};
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .list {
            flex-direction: column;
            column-count: 1;
          }
          .even-spacing .list {
            grid-template-columns: 1fr;
            row-gap: ${theme.spacing[3]};
          }
          .item + .item {
            margin-top: ${theme.spacing[3]};
          }
          .bullet-point {
            max-width: 100%;
          }
        }
      `}</style>
    </>
  )
}
